import React, { FC, Fragment, useState } from 'react';
import { Button, Box } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { updateDeliveryDateTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order } from 'models/order';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type ChangeDeliveryDateDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const ChangeDeliveryDateDialog: FC<ChangeDeliveryDateDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: ChangeDeliveryDateDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date(order?.deliveryDetails.date as number));
    const [hour, setHour] = useState<number | undefined>(order?.deliveryDetails.time?.hour as number);
    const [minute, setMinute] = useState<number | undefined>(order?.deliveryDetails.time?.minute as number);
    const { showBoundary } = useDenthubErrorBoundary();

    // Sync local state whenever dialog opens or order changes
    // Seeded when the dialog opens; a live order update (DD-3231) must not reset the picked date.
    useSeedOnOpen(open, () => {
        if (order) {
            setSelectedDate(new Date(order.deliveryDetails.date as number));
            setHour(order.deliveryDetails.time?.hour);
            setMinute(order.deliveryDetails.time?.minute);
        }
    });

    const [changeDeliveryDate] = useMutation(updateDeliveryDateTemplate, {
        onCompleted: ({ updateDeliveryDate }) => {
            handleClose();
            onUpdate?.(updateDeliveryDate);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeClick = async () => {
        await changeDeliveryDate({
            variables: {
                orderId: order?._id,
                deliveryDate: new Date(selectedDate).getTime(),
                time: {
                    hour: hour,
                    minute: minute,
                },
            },
        });
    };

    const handleDeliveryDateChange = (date: Date | null, hour: number | undefined, minute: number | undefined) => {
        if (date) {
            setSelectedDate(date as Date);
        }
        setHour(hour);
        setMinute(minute);
    };

    const handleClose = () => {
        setSelectedDate(new Date());
        setHour(undefined);
        setMinute(undefined);
        onClose();
    };

    return (
        <Fragment>
            <DHDialog
                fullWidth
                maxWidth="sm"
                open={open}
                title={translate('orders:CHANGE_DELIVERY_DATE')}
                onClose={handleClose}
                actions={
                    <Button
                        disabled={!selectedDate || selectedDate.toString() === 'Invalid Date'}
                        variant="contained"
                        onClick={handleChangeClick}
                        data-test="change-delivery-date-dialog.change-btn"
                    >
                        {translate('orders:CHANGE_DELIVERY_DATE')}
                    </Button>
                }
            >
                <Box>
                    <DeliveryDatePicker
                        onChange={handleDeliveryDateChange}
                        date={selectedDate}
                        hour={hour}
                        minute={minute}
                        lab={order?.supplier}
                        dataTest="change-delivery-date-dialog.delivery-date-picker"
                    />
                </Box>
            </DHDialog>
        </Fragment>
    );
};

export default ChangeDeliveryDateDialog;
